import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowUpRight,
  Info,
  LoaderCircle,
  RefreshCw,
  Wallet,
  WalletCards,
} from "lucide-react";
import { type ReactNode, useCallback, useId, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { z } from "zod";

import { OrganizationScopePicker } from "@/modules/dashboard/payments-config/components/OrganizationScopePicker";
import { useOrganizationsCatalog } from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import {
  ConsequenceList,
  ConsequenceRow,
} from "@/modules/dashboard/payout-batches/components/execute/ConsequenceList";
import { useArmDelay } from "@/modules/dashboard/payout-batches/hooks/useArmDelay";
import { useRuntimeWallets } from "@/modules/dashboard/wallets/hooks/useRuntimeWallets";
import type { RuntimeWallet } from "@/modules/dashboard/wallets/types/payments-wallets.types";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaAmountInput } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { ColocaTextareaField } from "@/shared/components/forms/ColocaTextareaField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaMoneyTransit } from "@/shared/components/motion/ColocaMoneyTransit";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { readApiStatus } from "@/shared/utils/apiError";
import { formatCurrencyAmount, minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { isPositiveDecimal } from "@/shared/utils/decimal";
import { formatRail } from "@/shared/utils/railDisplay";
import { mayHaveTakenEffect } from "@/shared/utils/unresolvedAttempt";

import { WALLET_DEBIT_CONCEPT_MAX, WALLET_DEBIT_NOTE_MAX } from "../constants/walletDebits.constants";
import { UnresolvedWalletDebitError, useCreateWalletDebit } from "../hooks/useCreateWalletDebit";
import {
  useWalletDebitSendPreview,
  type WalletDebitSendPreviewState,
} from "../hooks/useWalletDebitSendPreview";
import type {
  WalletDebit,
  WalletDebitCreated,
  WalletDebitSendCorridor,
  WalletDebitTarget,
} from "../types/walletDebits.types";
import {
  balanceAfterDebit,
  corridorOf,
  corridorValue,
  debitAmountProblem,
  OTHER_CORRIDOR,
} from "../utils/walletDebit";
import {
  isSameDebit,
  type WalletDebitAttempt,
  WalletDebitBusyError,
  type WalletDebitLookup,
} from "../utils/walletDebitAttempt";
import { WalletDebitCorridorField } from "./WalletDebitCorridorField";
import { WalletDebitSuccess } from "./WalletDebitSuccess";

const FORM_ID = "wallet-debit-form";

export interface WalletDebitDialogProps {
  /** Null closes the dialog. An organization (and a wallet) set here come preselected. */
  target: WalletDebitTarget | null;
  onClose: () => void;
  /** The debit was recorded (or found by checking a lost answer). */
  onCreated?: (debit: WalletDebitCreated) => void;
  /** Its proof changed after it was recorded. */
  onUpdated?: (debit: WalletDebit) => void;
}

export function WalletDebitDialog({ target, onClose, onCreated, onUpdated }: WalletDebitDialogProps) {
  if (!target) return null;
  return (
    <WalletDebitDialogContent
      key={`${target.organization.id}:${target.walletId ?? ""}`}
      target={target}
      onClose={onClose}
      onCreated={onCreated}
      onUpdated={onUpdated}
    />
  );
}

/** Opened from the history page, where nothing is preselected. */
export function NewWalletDebitDialog({
  open,
  ...props
}: Omit<WalletDebitDialogProps, "target"> & { open: boolean }) {
  if (!open) return null;
  return <WalletDebitDialogContent target={null} {...props} />;
}

type Step = "form" | "review" | "done";

interface FormValues {
  wallet_id: string;
  amount: string;
  /** `corridorValue` of the rail the send went through, or `OTHER_CORRIDOR`. */
  corridor: string;
  concept: string;
  internal_note: string;
}

type ContentProps = WalletDebitDialogProps;

/** The priced row of the chosen corridor in the current preview; null for none, or one that is not priced. */
function pricedCorridor(state: WalletDebitSendPreviewState, value: string): WalletDebitSendCorridor | null {
  if (state.status !== "ready") return null;
  const row = state.preview.corridors.find((corridor) => corridorValue(corridor) === value);
  return row?.net_amount ? row : null;
}

function WalletDebitDialogContent({ target, onClose, onCreated, onUpdated }: ContentProps) {
  const { t, activeLanguage } = useTranslations("walletDebits");
  const [organizationId, setOrganizationId] = useState<string | null>(target?.organization.id ?? null);
  const [step, setStep] = useState<Step>("form");
  const [submitted, setSubmitted] = useState<FormValues | null>(null);
  // The chosen corridor's price as reviewed; null for a send made some other way.
  const [submittedSend, setSubmittedSend] = useState<WalletDebitSendCorridor | null>(null);
  const [created, setCreated] = useState<WalletDebitCreated | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  // What the last check said about an unresolved debit, and whether the record on screen is one it found.
  const [lookup, setLookup] = useState<WalletDebitLookup | null>(null);
  const [checking, setChecking] = useState(false);
  const [recovered, setRecovered] = useState(false);
  const { armed, disarm } = useArmDelay(step === "review");
  const { submit, check, unresolved, twoFactor } = useCreateWalletDebit(organizationId);
  const runtime = useRuntimeWallets(organizationId ?? undefined);

  // What staff do with the debit on the last step (record its send, correct it, add proof) keeps the create's
  // answer on screen and tells the parent.
  const takeChange = useCallback(
    (debit: WalletDebit) => {
      setCreated((current) => (current ? { ...current, ...debit } : current));
      onUpdated?.(debit);
    },
    [onUpdated],
  );

  // The wallet the amount is checked against and the preview the corridor is; read by the schema, which is
  // built once.
  const walletRef = useRef<RuntimeWallet | null>(null);
  const previewRef = useRef<WalletDebitSendPreviewState>({ status: "idle" });
  const schema = useMemo(
    () =>
      z
        .object({
          wallet_id: z.string().min(1, t("validation_wallet")),
          amount: z.string(),
          corridor: z.string(),
          concept: z.string().trim().min(1, t("validation_concept")).max(WALLET_DEBIT_CONCEPT_MAX),
          internal_note: z.string().trim().min(1, t("validation_note")).max(WALLET_DEBIT_NOTE_MAX),
        })
        .superRefine((values, ctx) => {
          const wallet = walletRef.current;
          const currency = wallet?.currency ?? "COP";
          const problem = debitAmountProblem(values.amount, wallet?.balance_available ?? "0", currency);
          // Here rather than on the field, so a missing corridor never hides what is wrong with the amount.
          if (!values.corridor) {
            ctx.addIssue({ code: "custom", path: ["corridor"], message: t("validation_corridor") });
          } else if (
            // Reviewed against the price of this very amount: one being recalculated, or a corridor that
            // cannot be priced, cannot be authorized.
            !problem &&
            values.corridor !== OTHER_CORRIDOR &&
            !pricedCorridor(previewRef.current, values.corridor)
          ) {
            ctx.addIssue({
              code: "custom",
              path: ["corridor"],
              message: t("validation_corridor_unpriced"),
            });
          }
          if (!problem) return;
          ctx.addIssue({
            code: "custom",
            path: ["amount"],
            message:
              problem === "precision"
                ? t("validation_amount_precision", {
                    currency,
                    digits: String(minorUnitsFor(currency)),
                  })
                : problem === "exceeds"
                  ? t("validation_amount_exceeds", {
                      available: `${formatCurrencyAmount(wallet?.balance_available ?? "0", currency, activeLanguage)} ${currency}`,
                    })
                  : t("validation_amount"),
          });
        }),
    [t, activeLanguage],
  );
  const form = useZodForm(schema, {
    wallet_id: target?.walletId ?? "",
    amount: "",
    corridor: "",
    concept: t("concept_default"),
    internal_note: "",
  });

  const values = useWatch({ control: form.control });
  const walletId = values.wallet_id ?? "";
  const wallet = runtime.wallets.find((candidate) => candidate.id === walletId) ?? null;
  walletRef.current = wallet;
  const typedAmount = values.amount?.trim() ?? "";
  const sendPreview = useWalletDebitSendPreview(
    organizationId &&
      wallet &&
      typedAmount &&
      !debitAmountProblem(typedAmount, wallet.balance_available, wallet.currency)
      ? { organizationId, walletId: wallet.id, amount: typedAmount }
      : null,
  );
  previewRef.current = sendPreview;
  const organizationName =
    target?.organization.name ?? runtime.wallets[0]?.organization.name ?? t("organization_unknown");

  // What the form says, as the create's body.
  const bodyOf = (form: Partial<FormValues>) => {
    const corridor = corridorOf(form.corridor ?? "");
    return {
      organization_id: organizationId ?? "",
      wallet_id: form.wallet_id ?? "",
      amount: form.amount ?? "",
      rail: corridor?.rail ?? null,
      country: corridor?.country ?? null,
      concept: form.concept ?? "",
      internal_note: form.internal_note ?? "",
    };
  };
  // The unresolved debit is this very one: sending it again reuses its key, so it can only ever debit once.
  const isRetryOf = (attempt: WalletDebitAttempt | null, body: Partial<FormValues> | null) =>
    !!attempt && !!organizationId && !!body && isSameDebit(attempt.payload, bodyOf(body));
  // Any other debit to the organization waits until the unresolved one is checked.
  const blockedByUnresolved = !!unresolved && !isRetryOf(unresolved, values);

  const changeOrganization = (next: string | null) => {
    setOrganizationId(next);
    form.setValue("wallet_id", "");
    form.setValue("corridor", "");
    setError(null);
    setLookup(null);
  };

  // A debit is on record: show it, with what is left to do with it, and tell the parent.
  const finish = (debit: WalletDebitCreated, wasRecovered: boolean) => {
    disarm();
    setCreated(debit);
    setRecovered(wasRecovered);
    setStep("done");
    // The balance socket only carries the staff member's own organization, so the next debit would be
    // reviewed against the balance from before this one.
    void runtime.refreshOne(debit.wallet_id);
    onCreated?.(debit);
  };

  const checkUnresolved = async () => {
    setChecking(true);
    setLookup(null);
    try {
      const result = await check();
      if (result.kind !== "placed") {
        setLookup(result.kind);
        return;
      }
      finish(result.debit, true);
    } finally {
      setChecking(false);
    }
  };

  const toReview = (next: FormValues) => {
    if (blockedByUnresolved) return;
    setSubmitted(next);
    setSubmittedSend(pricedCorridor(sendPreview, next.corridor));
    setReviewed(false);
    setError(null);
    setStep("review");
  };

  const backToForm = () => {
    disarm();
    setStep("form");
    setError(null);
  };

  const confirm = async () => {
    if (!submitted || !organizationId) return;
    setSubmitting(true);
    setError(null);
    setLookup(null);
    try {
      const result = await submit(bodyOf(submitted), wallet?.currency ?? null);
      if (!result) return;
      finish(result, false);
    } catch (err) {
      if (err instanceof UnresolvedWalletDebitError) setError(t("unresolved_blocked"));
      // Nothing was sent: another tab held this organization's send for too long.
      else if (err instanceof WalletDebitBusyError) setError(t("debit_busy"));
      // No answer, or one that cannot rule the debit out: the unresolved notice says so and offers the check.
      else if (mayHaveTakenEffect(readApiStatus(err))) setError(null);
      else setError(HttpApiService.getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const startAnother = () => {
    form.reset({
      wallet_id: target?.walletId ?? "",
      amount: "",
      corridor: "",
      concept: t("concept_default"),
      internal_note: "",
    });
    setCreated(null);
    setSubmitted(null);
    setRecovered(false);
    setLookup(null);
    disarm();
    setStep("form");
  };

  // The code left and the challenge closed: the request is debiting the wallet.
  const processing = step === "review" && twoFactor.isRunning && !twoFactor.challenge.open;

  const title =
    step === "done"
      ? t("dialog_title_done")
      : processing
        ? t("dialog_title_processing")
        : step === "review"
          ? t("dialog_title_review")
          : t("dialog_title");
  const description =
    step === "done"
      ? t("dialog_description_done")
      : processing
        ? t("dialog_description_processing")
        : step === "review"
          ? t("dialog_description_review")
          : t("dialog_description");

  return (
    <>
      <ColocaDialog
        open
        onOpenChange={(open) => {
          if (!open && !submitting) onClose();
        }}
        size="lg"
        title={title}
        description={description}
        showCloseButton={!submitting}
        footer={
          processing ? null : step === "form" ? (
            <>
              <ColocaButton variant="ghost" onClick={onClose}>
                {t("action_cancel")}
              </ColocaButton>
              <ColocaButton
                type="submit"
                form={FORM_ID}
                variant="primary"
                disabled={!wallet || blockedByUnresolved || checking}
              >
                {t("action_review")}
              </ColocaButton>
            </>
          ) : step === "review" ? (
            <>
              <ColocaButton variant="ghost" icon={ArrowLeft} onClick={backToForm} disabled={submitting}>
                {t("action_back")}
              </ColocaButton>
              <ColocaButton
                variant="primary"
                icon={ArrowUpRight}
                loading={submitting}
                disabled={!reviewed || !armed || submitting || checking}
                onClick={() => void confirm()}
              >
                {t("action_confirm")}
              </ColocaButton>
            </>
          ) : (
            <>
              <ColocaButton variant="ghost" onClick={startAnother}>
                {t("action_another")}
              </ColocaButton>
              <ColocaButton variant="primary" onClick={onClose}>
                {t("action_close")}
              </ColocaButton>
            </>
          )
        }
      >
        {step === "form" && (
          <ColocaForm id={FORM_ID} form={form} onSubmit={toReview} className="space-y-5">
            {!target && <OrganizationField value={organizationId} onChange={changeOrganization} />}
            {target && (
              <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 px-4 py-3">
                <WalletCards className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">{t("field_organization")}</p>
                  <p className="truncate text-sm font-semibold text-foreground">{target.organization.name}</p>
                </div>
              </div>
            )}

            {unresolved && (
              <UnresolvedDebitNotice
                attempt={unresolved}
                retry={!blockedByUnresolved}
                lookup={lookup}
                checking={checking}
                onCheck={() => void checkUnresolved()}
                locale={activeLanguage}
              />
            )}
            {!unresolved && lookup === "not_placed" && (
              <ColocaAlert variant="secondary" icon={Info} text={t("unresolved_not_placed")} />
            )}

            {organizationId && (
              <WalletField
                wallets={runtime.wallets}
                isLoading={runtime.isLoading}
                error={runtime.error}
                locale={activeLanguage}
              />
            )}

            {wallet && (
              <>
                <ColocaAmountInput
                  name="amount"
                  label={t("field_amount")}
                  description={t("field_amount_hint", {
                    currency: wallet.currency,
                    available: `${formatCurrencyAmount(wallet.balance_available, wallet.currency, activeLanguage)} ${wallet.currency}`,
                  })}
                  suffix={wallet.currency}
                  maxFractionDigits={minorUnitsFor(wallet.currency)}
                />
                <WalletDebitCorridorField state={sendPreview} currency={wallet.currency} />
                <ColocaInputField
                  name="concept"
                  label={t("field_concept")}
                  description={t("field_concept_hint")}
                  maxLength={WALLET_DEBIT_CONCEPT_MAX}
                  required
                />
                <ColocaTextareaField
                  name="internal_note"
                  label={t("field_note")}
                  description={t("field_note_hint")}
                  placeholder={t("field_note_placeholder")}
                  maxLength={WALLET_DEBIT_NOTE_MAX}
                  rows={3}
                />
              </>
            )}

            {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}
          </ColocaForm>
        )}

        {processing && submitted && (
          <DebitProcessing organizationName={organizationName} wallet={wallet} amount={submitted.amount} />
        )}

        {step === "review" && !processing && submitted && wallet && (
          <DebitReview
            organizationName={organizationName}
            wallet={wallet}
            values={submitted}
            send={submittedSend}
            reviewed={reviewed}
            onReviewedChange={setReviewed}
            disabled={submitting || checking}
            error={error}
            notice={
              unresolved && isRetryOf(unresolved, submitted) ? (
                <UnresolvedDebitNotice
                  attempt={unresolved}
                  retry
                  lookup={lookup}
                  checking={checking}
                  onCheck={() => void checkUnresolved()}
                  locale={activeLanguage}
                />
              ) : null
            }
          />
        )}

        {step === "done" && created && (
          <WalletDebitSuccess debit={created} recovered={recovered} onChange={takeChange} />
        )}
      </ColocaDialog>
      <TwoFactorActionModals
        controller={twoFactor}
        requiredDescription={t("two_factor_required")}
        challengeDescription={
          submitted && wallet
            ? t("two_factor_description", {
                amount: formatCurrencyAmount(submitted.amount, wallet.currency, activeLanguage),
                currency: wallet.currency,
                name: organizationName,
              })
            : undefined
        }
      />
    </>
  );
}

function OrganizationField({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const { t } = useTranslations("walletDebits");
  const { t: tBatches } = useTranslations("payoutBatches");
  const pickerId = useId();
  const catalog = useOrganizationsCatalog();
  return (
    <OrganizationScopePicker
      id={pickerId}
      mode="required"
      value={value}
      onValueChange={onChange}
      catalog={catalog}
      labels={{
        label: t("field_organization"),
        placeholder: tBatches("org_picker_placeholder"),
        loading: tBatches("org_picker_loading"),
        empty: tBatches("org_picker_empty"),
        searching: tBatches("org_picker_searching"),
        searchError: tBatches("org_picker_search_error"),
        catalogError: tBatches("org_picker_catalog_error"),
        catalogTruncated: tBatches("org_picker_catalog_truncated"),
        requiredHint: t("field_organization_required_hint"),
        orgHint: t("field_organization_hint"),
      }}
    />
  );
}

interface WalletFieldProps {
  wallets: RuntimeWallet[];
  isLoading: boolean;
  error: string | null;
  locale: string;
}

/** Every wallet of the organization, in any currency; only an active one with money available can be picked. */
function WalletField({ wallets, isLoading, error, locale }: WalletFieldProps) {
  const { t } = useTranslations("walletDebits");
  if (isLoading) return <ColocaSkeleton className="h-16 w-full" />;
  if (error) return <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />;
  if (wallets.length === 0) {
    return <ColocaAlert variant="warning" icon={Info} text={t("field_wallet_none")} />;
  }
  return (
    <ColocaSelectField
      name="wallet_id"
      label={t("field_wallet")}
      description={t("field_wallet_hint")}
      placeholder={t("field_wallet_placeholder")}
      required
      items={wallets.map((wallet) => {
        const funded = isPositiveDecimal(wallet.balance_available);
        return {
          value: wallet.id,
          disabled: wallet.status !== "active" || !funded,
          label:
            wallet.status !== "active"
              ? t("field_wallet_option_inactive", {
                  currency: wallet.currency,
                  status: t(wallet.status === "frozen" ? "wallet_frozen" : "wallet_closed"),
                })
              : funded
                ? t("field_wallet_option", {
                    currency: wallet.currency,
                    balance: formatCurrencyAmount(wallet.balance_available, wallet.currency, locale),
                  })
                : t("field_wallet_option_empty", { currency: wallet.currency }),
        };
      })}
    />
  );
}

interface DebitReviewProps {
  organizationName: string;
  wallet: RuntimeWallet;
  values: FormValues;
  /** The chosen corridor's price; null for a send made some other way, which pays no fees. */
  send: WalletDebitSendCorridor | null;
  reviewed: boolean;
  onReviewedChange: (checked: boolean) => void;
  disabled: boolean;
  error: string | null;
  /** The unresolved notice, when this review is the retry of a debit whose answer was lost. */
  notice?: ReactNode;
}

/**
 * The send's reference leads: it is what ties this debit to the money that already left, and the one value a
 * typo here would make impossible to reconcile later.
 */
function DebitReview({
  organizationName,
  wallet,
  values,
  send,
  reviewed,
  onReviewedChange,
  disabled,
  error,
  notice,
}: DebitReviewProps) {
  const { t } = useTranslations("walletDebits");
  const digits = minorUnitsFor(wallet.currency);
  const amountProps = { currency: wallet.currency, fractionDigits: digits };
  return (
    <div className="space-y-5">
      <ConsequenceList>
        <ConsequenceRow label={t("review_organization")} value={organizationName} />
        <ConsequenceRow
          label={t("review_wallet")}
          value={t("review_wallet_value", { currency: wallet.currency })}
        />
        <ConsequenceRow
          label={t("review_balance_before")}
          value={<ColocaAmountDisplay value={wallet.balance_available} {...amountProps} size="sm" />}
        />
        <ConsequenceRow
          label={t("review_amount")}
          value={
            <span className="text-destructive">
              <ColocaAmountDisplay value={`-${values.amount.trim()}`} {...amountProps} size="sm" signed />
            </span>
          }
        />
        <ConsequenceRow
          label={t("review_balance_after")}
          emphasis
          value={
            <ColocaAmountDisplay
              value={balanceAfterDebit(wallet.balance_available, values.amount.trim())}
              {...amountProps}
              size="lg"
            />
          }
        />
      </ConsequenceList>

      <ConsequenceList>
        <ConsequenceRow
          label={t("review_corridor")}
          value={send ? formatRail(send.rail) : t("corridor_other")}
        />
        {send && (
          <>
            <ConsequenceRow
              label={t("review_commission")}
              value={<ColocaAmountDisplay value={send.commission ?? "0"} {...amountProps} size="sm" />}
            />
            <ConsequenceRow
              label={t("review_taxes")}
              value={<ColocaAmountDisplay value={send.tax_total ?? "0"} {...amountProps} size="sm" />}
            />
          </>
        )}
        <ConsequenceRow
          label={t("review_send_by_hand")}
          emphasis
          value={
            <ColocaAmountDisplay
              value={send?.net_amount ?? values.amount.trim()}
              {...amountProps}
              size="lg"
            />
          }
        />
      </ConsequenceList>

      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <div className="min-w-0">
          <dt className="text-xs text-muted-foreground">{t("review_concept")}</dt>
          <dd className="mt-0.5 break-words font-medium text-foreground">{values.concept.trim()}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-muted-foreground">{t("review_note")}</dt>
          <dd className="mt-0.5 break-words text-foreground">{values.internal_note.trim()}</dd>
        </div>
      </dl>

      <ColocaAlert variant="secondary" icon={Info} text={t("review_client_sees")} />
      <ColocaAlert variant="warning" icon={AlertTriangle} text={t("review_held")} />
      {notice}
      {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}

      <ColocaCheckbox
        checked={reviewed}
        onCheckedChange={onReviewedChange}
        disabled={disabled}
        label={t("review_checkbox")}
      />
    </div>
  );
}

interface DebitProcessingProps {
  organizationName: string;
  wallet: RuntimeWallet | null;
  amount: string;
}

/** While the authorized debit is on its way: the money leaving the client's wallet for the manual send. */
function DebitProcessing({ organizationName, wallet, amount }: DebitProcessingProps) {
  const { t, activeLanguage } = useTranslations("walletDebits");
  if (!wallet) {
    return (
      <div role="status" className="flex justify-center py-10">
        <LoaderCircle aria-hidden="true" className="size-8 animate-spin text-primary" />
      </div>
    );
  }
  return (
    <div role="status" className="flex flex-col items-center gap-6 py-6 text-center">
      <ColocaMoneyTransit
        className="max-w-md"
        label={t("processing_transit_aria", {
          amount: formatCurrencyAmount(amount, wallet.currency, activeLanguage),
          currency: wallet.currency,
          name: organizationName,
        })}
        from={{
          icon: <Wallet className="size-5" />,
          label: organizationName,
          caption: t("review_wallet_value", { currency: wallet.currency }),
        }}
        to={{ icon: <ArrowUpRight className="size-5" />, label: t("processing_to") }}
        legs={[{ rail: { kind: "fiat" }, asset: { kind: "fiat", currency: wallet.currency } }]}
      />
      <div className="text-foreground">
        <ColocaAmountDisplay
          value={`-${amount.trim()}`}
          currency={wallet.currency}
          fractionDigits={minorUnitsFor(wallet.currency)}
          size="lg"
          signed
        />
      </div>
    </div>
  );
}

interface UnresolvedDebitNoticeProps {
  attempt: WalletDebitAttempt;
  /** The form holds that same debit: authorizing it again is safe. */
  retry: boolean;
  lookup: WalletDebitLookup | null;
  checking: boolean;
  onCheck: () => void;
  locale: string;
}

/** A debit to this organization whose answer was lost: it may have gone through, so it is checked first. */
function UnresolvedDebitNotice({
  attempt,
  retry,
  lookup,
  checking,
  onCheck,
  locale,
}: UnresolvedDebitNoticeProps) {
  const { t } = useTranslations("walletDebits");
  const { payload, currency } = attempt;
  const amount = currency
    ? `${formatCurrencyAmount(payload.amount, currency, locale)} ${currency}`
    : payload.amount;
  const status =
    lookup === "pending" ? t("unresolved_pending") : lookup === "unknown" ? t("unresolved_unknown") : null;
  return (
    <ColocaAlert
      variant="warning"
      icon={AlertTriangle}
      heading={t("unresolved_title")}
      text={
        <>
          <span className="block">
            {retry
              ? t("unresolved_text_retry")
              : t("unresolved_text", { amount, reference: payload.external_reference ?? payload.concept })}
          </span>
          {status && (
            <span className="mt-1.5 block font-medium" role="status">
              {status}
            </span>
          )}
        </>
      }
      actions={
        <ColocaButton size="sm" variant="secondary" icon={RefreshCw} loading={checking} onClick={onCheck}>
          {t("unresolved_action")}
        </ColocaButton>
      }
    />
  );
}
